import React from "react";
import { Box, Typography } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { summaryCardTheme as t } from "./styles";

const CATEGORIES = [
  { key: "security", label: "Security failed", color: t.colors.chart[0] },
  { key: "compliance", label: "Compliance failed", color: t.colors.chart[1] },
  { key: "cost", label: "Cost", color: t.colors.success },
];

function SummaryArnDonut({ overall }) {
  const data = React.useMemo(() => {
    if (!overall) return [];
    const security = overall.arns_security_failed ?? 0;
    const compliance = overall.arns_compliance_failed ?? 0;
    const cost = overall.arns_cost ?? 0;
    return [
      { name: CATEGORIES[0].label, value: security, fill: CATEGORIES[0].color },
      { name: CATEGORIES[1].label, value: compliance, fill: CATEGORIES[1].color },
      { name: CATEGORIES[2].label, value: cost, fill: CATEGORIES[2].color },
    ].filter((d) => d.value > 0);
  }, [overall]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    return (
      <Box
        sx={{
          ...t.chartTooltip,
          fontFamily: t.fontFamily,
        }}
      >
        <Typography component="span" sx={{ fontWeight: 600, fontSize: "0.75rem", color: t.colors.text }}>
          {d.name}
        </Typography>
        <Typography component="span" sx={{ fontSize: "0.75rem", color: t.colors.textMuted, ml: 0.5 }}>
          — {d.value} ARNs
        </Typography>
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
          fontFamily: t.fontFamily,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No ARN distribution data
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        fontFamily: t.fontFamily,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: t.colors.text,
          fontSize: "0.875rem",
          mb: 1,
          fontFamily: t.fontFamily,
        }}
      >
        ARNs by category
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: t.colors.textMuted,
          fontSize: "0.75rem",
          mb: 2,
          fontFamily: t.fontFamily,
        }}
      >
        Resources with security, compliance, or cost flags
      </Typography>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={56}
            outerRadius={72}
            paddingAngle={2}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default SummaryArnDonut;
